import { gql } from "@apollo/client";

/**
 * Certificate / Saarthi Pro expiry tracker.
 *
 * Both lists return the SAME shape (CertExpiryVehicle on the backend), which is
 * why one fragment and one table serve both views — the only difference is
 * which expiry the rows are sorted and filtered on.
 *
 * Every date field is epoch SECONDS.
 */
const EXPIRY_FIELDS = `
  vin
  imei
  serialNumber
  deviceModel
  iccid
  msisdn1
  vehicleModel
  vehicleType
  yearOfManufacture
  customerName
  customerMobile
  dealerName
  dealerContactNumber
  registrationState
  rtoName
  claUrl
  claIssuedOn
  claExpiresOn
  claDaysToExpiry
  saarthiProStartDate
  saarthiProExpiryDate
  saarthiProDaysToExpiry
  saarthiProStatus
  simActivationStatus
  daysToExpiry
  severity
  lastCallAt
  lastCallNote
  lastCallBy
  lastCallReason
  lastCallAttendedBy
`;

/**
 * Why the customer is not recharging, offered as a fixed list so the answers
 * are countable instead of 500 spellings of "too expensive".
 *
 * This is the whole vocabulary — edit it here and the dropdown follows. It is
 * stored as free text in `call_logs.caller_response`, deliberately NOT as a
 * database enum, so changing this list never needs a migration. Anything that
 * does not fit goes under "Other" and gets explained in the call notes.
 */
export const CALL_REASONS = [
  'Cost too high',
  'Vehicle sold or transferred',
  'Vehicle off-road / not in use',
  'Wants to renew later',
  'Not aware it was required — explained',
  'Using another service provider',
  'Payment / bank issue',
  'Customer not reachable',
  'Wrong number on record',
  'Refused — no reason given',
  'Other (see notes)',
];

export const GET_EXPIRING_CERTIFICATES = gql`
  query ExpiringCertificates(
    $offset: Int
    $limit: Int
    $search: String
    $severity: String
    $windowDays: Int
    $includeExpired: Boolean
  ) {
    expiringCertificates(
      offset: $offset
      limit: $limit
      search: $search
      severity: $severity
      windowDays: $windowDays
      includeExpired: $includeExpired
    ) {
      success
      count
      error
      data { ${EXPIRY_FIELDS} }
    }
  }
`;

export const GET_EXPIRING_SAARTHI_PRO = gql`
  query ExpiringSaarthiPro(
    $offset: Int
    $limit: Int
    $search: String
    $severity: String
    $windowDays: Int
    $includeExpired: Boolean
  ) {
    expiringSaarthiPro(
      offset: $offset
      limit: $limit
      search: $search
      severity: $severity
      windowDays: $windowDays
      includeExpired: $includeExpired
    ) {
      success
      count
      error
      data { ${EXPIRY_FIELDS} }
    }
  }
`;

export const GET_CERT_EXPIRY_STATS = gql`
  query CertificateExpiryStats($kind: String, $windowDays: Int) {
    certificateExpiryStats(kind: $kind, windowDays: $windowDays) {
      criticalCount
      warningCount
      expiredCount
      contactedTodayCount
    }
  }
`;

/**
 * A real mutation, unlike the partner portal's `logCall`, which the
 * recharge-service API declares as a query even though it writes.
 */
export const LOG_EXPIRY_CALL = gql`
  mutation LogCertificateExpiryCall(
    $vin: String!
    $imei: String
    $callType: String
    $callNote: String!
    $callerResponse: String
    $attendedBy: String
  ) {
    logCertificateExpiryCall(
      vin: $vin
      imei: $imei
      callType: $callType
      callNote: $callNote
      callerResponse: $callerResponse
      attendedBy: $attendedBy
    ) {
      success
      message
      error
    }
  }
`;

/**
 * Certificate issue dates are pulled from the legacy OEM tool by a cron every
 * three hours. This forces that pull NOW, for when a batch of certificates has
 * just been uploaded there and the calling team is looking at the list.
 *
 * It returns IMMEDIATELY with status "running" — a full sweep is ~11.7k rows
 * and takes 3-7 minutes, so nothing can await it. Poll GET_CERT_REFRESH_STATUS
 * for the outcome.
 *
 * Saarthi Pro dates are NOT part of this: they come from Sensorise on their own
 * 3-hourly cron, not from the legacy OEM tool. Hence the button only appears on
 * the certificate tab.
 */
export const REFRESH_CERTIFICATE_DATA = gql`
  mutation RefreshCertificateData {
    refreshCertificateData {
      status
      triggeredBy
      startedAt
      finishedAt
      written
      renewed
      unchanged
      failed
      error
    }
  }
`;

/**
 * State of the last refresh — manual OR cron, so this doubles as "when was this
 * data last pulled". Poll it while `status` is "running".
 */
export const GET_CERT_REFRESH_STATUS = gql`
  query CertificateRefreshStatus {
    certificateRefreshStatus {
      status
      triggeredBy
      startedAt
      finishedAt
      written
      renewed
      unchanged
      failed
      error
    }
  }
`;

export type ExpiryKind = "ais" | "saarthi_pro";

export type Severity = "expired" | "critical" | "warning" | "normal";

export type ExpiryVehicle = {
  vin: string | null;
  imei: string | null;
  serialNumber: string | null;
  deviceModel: string | null;
  iccid: string | null;
  msisdn1: string | null;
  vehicleModel: string | null;
  vehicleType: string | null;
  yearOfManufacture: number | null;
  customerName: string | null;
  customerMobile: string | null;
  dealerName: string | null;
  dealerContactNumber: string | null;
  registrationState: string | null;
  rtoName: string | null;
  claUrl: string | null;
  claIssuedOn: number | null;
  claExpiresOn: number | null;
  claDaysToExpiry: number | null;
  saarthiProStartDate: number | null;
  saarthiProExpiryDate: number | null;
  saarthiProDaysToExpiry: number | null;
  saarthiProStatus: string | null;
  simActivationStatus: string | null;
  daysToExpiry: number | null;
  severity: Severity | null;
  lastCallAt: number | null;
  lastCallNote: string | null;
  /** The agent, from their login. */
  lastCallBy: string | null;
  /** Why the customer last declined to recharge. */
  lastCallReason: string | null;
  /** Who answered at the customer's end. */
  lastCallAttendedBy: string | null;
};

export type ExpiryResponse = {
  success: boolean;
  data: ExpiryVehicle[];
  count: number;
  error: string | null;
};

export type ExpiringCertificatesData = { expiringCertificates: ExpiryResponse };
export type ExpiringSaarthiProData = { expiringSaarthiPro: ExpiryResponse };

export type CertExpiryStats = {
  criticalCount: number;
  warningCount: number;
  expiredCount: number;
  contactedTodayCount: number;
};

export type CertExpiryStatsData = { certificateExpiryStats: CertExpiryStats };

export type CertRefreshStatus = {
  /** "idle" (none since the server booted) | "running" | "done" | "failed". */
  status: "idle" | "running" | "done" | "failed";
  /** A username, or "cron". */
  triggeredBy: string | null;
  /** ISO 8601 — NOT epoch seconds, unlike every other date on this page. */
  startedAt: string | null;
  finishedAt: string | null;
  /** Null while running. */
  written: number | null;
  /** Of those, vehicles re-certified upstream — the ones leaving this list. */
  renewed: number | null;
  unchanged: number | null;
  failed: number | null;
  error: string | null;
};

export type CertRefreshStatusData = {
  certificateRefreshStatus: CertRefreshStatus;
};
export type RefreshCertificateDataResult = {
  refreshCertificateData: CertRefreshStatus;
};
